/**
 * Pure utility functions for detecting and processing links in text.
 * These are platform-agnostic and can be used by both mobile and web.
 */

/**
 * Regex pattern to match URLs in text
 * Matches:
 * - URLs with protocols (http://, https://, ftp://)
 * - URLs without protocols (e.g., example.com, www.example.com)
 * - URLs with paths, query strings, and fragments
 * - Common TLDs (must start with a letter to avoid matching version numbers like "1.0.4")
 */
export const URL_REGEX = /(?:(?:https?|ftp):\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z][a-zA-Z0-9]{1,5}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/\/=]*)/gi

/**
 * Regex pattern to match email addresses
 * Matches standard email formats
 */
export const EMAIL_REGEX = /[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*/gi

/**
 * Combined regex pattern to match both URLs and email addresses
 */
export const URL_OR_EMAIL_REGEX = new RegExp(`(${EMAIL_REGEX.source})|(${URL_REGEX.source})`, 'gi')

/**
 * Cleans up a URL by stripping trailing punctuation that is likely part of
 * surrounding prose rather than the URL itself.
 *
 * Handles:
 * - Trailing punctuation: . , ! ? : ;
 * - Unbalanced closing parentheses (e.g., when URL is wrapped in parens)
 *
 * @param url - The raw URL match to clean
 * @returns Object with cleaned URL and number of characters stripped
 */
export function cleanUrlMatch(url: string): { cleanedUrl: string; strippedCount: number } {
    let cleanedUrl = url
    let strippedCount = 0

    // Keep stripping trailing characters that are likely prose punctuation
    while (cleanedUrl.length > 0) {
        const lastChar = cleanedUrl[cleanedUrl.length - 1]

        // Always strip these trailing punctuation marks
        if (['.', ',', '!', '?', ':', ';'].includes(lastChar)) {
            cleanedUrl = cleanedUrl.slice(0, -1)
            strippedCount++
            continue
        }

        // Strip closing paren only if unbalanced (more ) than ()
        if (lastChar === ')') {
            const openCount = (cleanedUrl.match(/\(/g) || []).length
            const closeCount = (cleanedUrl.match(/\)/g) || []).length
            if (closeCount > openCount) {
                cleanedUrl = cleanedUrl.slice(0, -1)
                strippedCount++
                continue
            }
        }

        // No more characters to strip
        break
    }

    return { cleanedUrl, strippedCount }
}

/**
 * Checks if a URL points to a GIF image
 * @param url - The URL to check
 * @returns true if the URL likely points to a GIF
 */
export function isGifUrl(url: string | null | undefined): boolean {
    if (!url) return false

    // Remove query parameters and fragments for cleaner matching
    const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase()

    // Check if URL ends with .gif
    if (cleanUrl.endsWith('.gif')) {
        return true
    }

    // Check for common GIF hosting patterns
    // Giphy URLs
    if (cleanUrl.includes('giphy.com') && cleanUrl.includes('/media/')) {
        return true
    }

    // Tenor GIFs
    if (cleanUrl.includes('tenor.com') && cleanUrl.includes('.gif')) {
        return true
    }

    // Direct imgur GIFs
    if (cleanUrl.includes('imgur.com') && cleanUrl.endsWith('.gif')) {
        return true
    }

    return false
}

/**
 * Counts the number of URLs in a text string (ignoring email addresses)
 * @param text - The text to search for URLs
 * @returns The number of URLs found
 */
export function countUrls(text: string | null | undefined): number {
    if (!text) return 0

    const matches = Array.from(text.matchAll(URL_OR_EMAIL_REGEX))
    let count = 0

    for (const match of matches) {
        const matchText = match[0]
        const isEmail = EMAIL_REGEX.test(matchText)
        EMAIL_REGEX.lastIndex = 0

        if (!isEmail) {
            count++
        }
    }

    return count
}

/**
 * Extracts all URLs from a text string (ignoring email addresses)
 * @param text - The text to search for URLs
 * @param maxCount - Maximum number of URLs to extract (default: 5)
 * @returns Array of URLs found (with protocol added if missing)
 */
export function extractAllUrls(text: string | null | undefined, maxCount: number = 5): string[] {
    if (!text) return []

    const matches = Array.from(text.matchAll(URL_OR_EMAIL_REGEX))
    const urls: string[] = []

    for (const match of matches) {
        if (urls.length >= maxCount) break

        const matchText = match[0]
        const isEmail = EMAIL_REGEX.test(matchText)
        EMAIL_REGEX.lastIndex = 0

        if (!isEmail) {
            // Clean up trailing punctuation
            const { cleanedUrl } = cleanUrlMatch(matchText)
            let url = cleanedUrl
            if (!url.match(/^[a-zA-Z]+:\/\//)) {
                url = `https://${url}`
            }
            urls.push(url)
        }
    }

    return urls
}

/**
 * Extracts the first URL from a text string (ignoring email addresses)
 * @param text - The text to search for URLs
 * @returns The first URL found (with protocol added if missing) or null if no URL found
 */
export function extractFirstUrl(text: string | null | undefined): string | null {
    if (!text) return null

    const matches = Array.from(text.matchAll(URL_OR_EMAIL_REGEX))

    // Find the first match that is NOT an email
    for (const match of matches) {
        const matchText = match[0]
        const isEmail = EMAIL_REGEX.test(matchText)
        EMAIL_REGEX.lastIndex = 0 // Reset regex state

        if (!isEmail) {
            // Clean up trailing punctuation
            const { cleanedUrl } = cleanUrlMatch(matchText)
            let url = cleanedUrl
            // Add https:// if no protocol is specified
            if (!url.match(/^[a-zA-Z]+:\/\//)) {
                url = `https://${url}`
            }
            return url
        }
    }

    return null
}

/**
 * Regex pattern to match Markdown-style links [text](url)
 * Must be processed BEFORE plain URLs to avoid partial matches
 */
export const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\(([^)]+)\)/g

/**
 * Type for a text part that can be regular text, URL, email, or Markdown link
 */
export type TextPart = {
    text: string
    isUrl: boolean
    isEmail?: boolean
    isMarkdownLink?: boolean
    linkUrl?: string // The actual URL for Markdown links (text is the display text)
}

/**
 * Splits text into parts, highlighting the first URL
 * @param text - The text to process
 * @returns An array of text parts with their types
 */
export function splitTextWithFirstUrl(text: string | null | undefined): TextPart[] {
    if (!text) return []

    const matches = Array.from(text.matchAll(URL_OR_EMAIL_REGEX))
    if (matches.length === 0) {
        return [{ text, isUrl: false }]
    }

    const firstMatch = matches[0]
    const startIndex = firstMatch.index || 0
    const rawMatchText = firstMatch[0]

    // Check if it's an email
    const isEmail = EMAIL_REGEX.test(rawMatchText)
    EMAIL_REGEX.lastIndex = 0 // Reset regex state

    // Clean up URL to strip trailing punctuation (don't clean emails)
    const { cleanedUrl, strippedCount } = isEmail
        ? { cleanedUrl: rawMatchText, strippedCount: 0 }
        : cleanUrlMatch(rawMatchText)

    // End index is adjusted for cleaned URL
    const endIndex = startIndex + rawMatchText.length - strippedCount

    const parts: TextPart[] = []

    // Text before the URL/email
    if (startIndex > 0) {
        parts.push({ text: text.substring(0, startIndex), isUrl: false })
    }

    // The URL/email itself (cleaned)
    parts.push({
        text: cleanedUrl,
        isUrl: !isEmail,
        isEmail: isEmail
    })

    // Text after the URL/email
    if (endIndex < text.length) {
        parts.push({ text: text.substring(endIndex), isUrl: false })
    }

    return parts
}

/**
 * Splits text into parts, highlighting ALL URLs, emails, and Markdown links
 * Markdown links [text](url) are processed first, then plain URLs/emails
 * @param text - The text to process
 * @returns An array of text parts with their types
 */
export function splitTextWithAllUrls(text: string | null | undefined): TextPart[] {
    if (!text) return []

    // First pass: find all Markdown links and mark their positions
    const markdownMatches: Array<{ start: number; end: number; displayText: string; url: string }> = []
    const mdRegex = new RegExp(MARKDOWN_LINK_REGEX.source, 'g')
    let mdMatch: RegExpExecArray | null

    while ((mdMatch = mdRegex.exec(text)) !== null) {
        markdownMatches.push({
            start: mdMatch.index,
            end: mdMatch.index + mdMatch[0].length,
            displayText: mdMatch[1],
            url: mdMatch[2]
        })
    }

    // Helper to check if a position is inside a Markdown link
    const isInsideMarkdownLink = (pos: number): boolean => {
        return markdownMatches.some(m => pos >= m.start && pos < m.end)
    }

    // Second pass: find all plain URLs/emails (excluding those inside Markdown links)
    const urlMatches = Array.from(text.matchAll(URL_OR_EMAIL_REGEX))
    const filteredUrlMatches = urlMatches.filter(match => {
        const pos = match.index || 0
        return !isInsideMarkdownLink(pos)
    })

    // Combine all matches and sort by position
    type MatchInfo = {
        type: 'markdown' | 'url' | 'email'
        start: number
        end: number
        text: string
        url?: string
    }

    const allMatches: MatchInfo[] = []

    // Add Markdown links
    for (const md of markdownMatches) {
        allMatches.push({
            type: 'markdown',
            start: md.start,
            end: md.end,
            text: md.displayText,
            url: md.url
        })
    }

    // Add plain URLs/emails
    for (const match of filteredUrlMatches) {
        const startIndex = match.index || 0
        const rawMatchText = match[0]

        // Check if it's an email
        const isEmail = EMAIL_REGEX.test(rawMatchText)
        EMAIL_REGEX.lastIndex = 0

        // Clean up URL to strip trailing punctuation (don't clean emails)
        const { cleanedUrl, strippedCount } = isEmail
            ? { cleanedUrl: rawMatchText, strippedCount: 0 }
            : cleanUrlMatch(rawMatchText)

        const endIndex = startIndex + rawMatchText.length - strippedCount

        allMatches.push({
            type: isEmail ? 'email' : 'url',
            start: startIndex,
            end: endIndex,
            text: cleanedUrl
        })
    }

    // Sort by start position
    allMatches.sort((a, b) => a.start - b.start)

    // If no matches, return original text
    if (allMatches.length === 0) {
        return [{ text, isUrl: false }]
    }

    // Build parts array
    const parts: TextPart[] = []
    let lastIndex = 0

    for (const match of allMatches) {
        // Text before this match
        if (match.start > lastIndex) {
            parts.push({ text: text.substring(lastIndex, match.start), isUrl: false })
        }

        // The match itself
        if (match.type === 'markdown') {
            parts.push({
                text: match.text,
                isUrl: true,
                isMarkdownLink: true,
                linkUrl: match.url
            })
        } else if (match.type === 'email') {
            parts.push({
                text: match.text,
                isUrl: false,
                isEmail: true
            })
        } else {
            parts.push({
                text: match.text,
                isUrl: true
            })
        }

        lastIndex = match.end
    }

    // Text after the last match
    if (lastIndex < text.length) {
        parts.push({ text: text.substring(lastIndex), isUrl: false })
    }

    return parts
}
